import React, { useState, useEffect, useCallback } from 'react';
import { Home } from 'lucide-react';
import sendQCChecklistEmail from './qcChecklistWordService';
import { validateFormCompletion, markFormCompleted, lookupVesselBySerial, isVesselScrapped, getScrappedFormMessage } from './formTraceabilityService';
import { getCurrentUserDisplayName } from '../utils/currentUser';
import SerialValidationModal from './SerialValidationModal';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';

const FinalQCChecklistForm = ({ onBack }) => {
  const [formData, setFormData] = useState({
    serialNumber: '',
    customerName: '',
    waterCapacity: '',
    dateCompleted: '',
    qcInspector: '',
    vesselStatus: 'Under Vacuum',

    checklistItems: {
      serialNumberStamped: false,
      dataPlatePresent: false,
      testCertificateAttached: false,
      paintFinishComplete: false,
      ralColourCorrect: false,
      labelsDecalsApplied: false,
      valvesInstalledTorqued: false,
      valvesSealed: false,
      reliefValvePresent: false,
      multivalveOrientation: false,
      protectiveHoodInstalled: false,
      tankSafeTransport: false,
      liftingLugsInspected: false,
      noDamageHandling: false
    },
    
    inspectorName: '',
    inspectorDate: '',
    inspectorSignature: '',
    secondCheck: '',
    
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [serialModalOpen, setSerialModalOpen] = useState(false);
  const [serialModalMessage, setSerialModalMessage] = useState('');
  const [serialLookupState, setSerialLookupState] = useState({ loading: false, notFound: false, lastLookup: '' });
  const [missingVesselFields, setMissingVesselFields] = useState({});
  const [freshEyesBlock, setFreshEyesBlock] = useState({ blocked: false, completedBy: '' });
  const MISSING_PLACEHOLDER = 'Unable to obtain — please enter';
  const closeSerialModal = useCallback(() => setSerialModalOpen(false), []);

  useEffect(() => {
    const name = getCurrentUserDisplayName();
    if (!name) return;
    setFormData(prev => prev.qcInspector ? prev : { ...prev, qcInspector: name });
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('checklistItems.')) {
      const itemName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        checklistItems: {
          ...prev.checklistItems,
          [itemName]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
      if (name === 'serialNumber' && serialLookupState.lastLookup) {
        setSerialLookupState({ loading: false, notFound: false, lastLookup: '' });
        setMissingVesselFields({});
        setFreshEyesBlock({ blocked: false, completedBy: '' });
      }
    }
  };

  const handleSerialLookup = useCallback(async () => {
    const serial = String(formData.serialNumber || '').trim();
    if (!serial || serial === serialLookupState.lastLookup) return;

    setSerialLookupState({ loading: true, notFound: false, lastLookup: serial });
    try {
      const result = await lookupVesselBySerial(serial);
      if (!result.found) {
        setSerialLookupState({ loading: false, notFound: true, lastLookup: serial });
        setMissingVesselFields({});
        setFreshEyesBlock({ blocked: false, completedBy: '' });
        return;
      }

      const vessel = result.vessel || {};
      const missing = {};
      const hasOwner = !!vessel.owner;
      const hasVesselSize = vessel.vesselSize !== null && vessel.vesselSize !== undefined && vessel.vesselSize !== '';
      if (!hasOwner) missing.customerName = true;
      if (!hasVesselSize) missing.waterCapacity = true;

      const tankCertBy = (result.formProgress?.tankCertification?.completedBy || '').trim();
      const currentUser = getCurrentUserDisplayName().trim();
      const sameUser = !!tankCertBy && !!currentUser && tankCertBy.toLowerCase() === currentUser.toLowerCase();

      setFormData(prev => {
        const next = { ...prev };
        if (!prev.customerName && hasOwner) {
          next.customerName = vessel.owner;
        }
        if ((prev.waterCapacity === '' || prev.waterCapacity === null || prev.waterCapacity === undefined)
            && hasVesselSize) {
          next.waterCapacity = String(vessel.vesselSize);
        }
        return next;
      });
      setMissingVesselFields(missing);
      setFreshEyesBlock({ blocked: sameUser, completedBy: tankCertBy });
      setSerialLookupState({ loading: false, notFound: false, scrapped: isVesselScrapped(vessel), lastLookup: serial });
    } catch (error) {
      console.warn('Vessel lookup failed:', error);
      setSerialLookupState({ loading: false, notFound: false, lastLookup: serial });
    }
  }, [formData.serialNumber, serialLookupState.lastLookup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    let validationResult;
    try {
      validationResult = await validateFormCompletion(formData.serialNumber, 'finalQCChecklist');
    } catch (error) {
      console.error('Serial validation failed:', error);
      setSerialModalMessage(error.message || 'Vessel Serial Number not found in Vessel Inventory.');
      setSerialModalOpen(true);
      setIsSubmitting(false);
      return;
    }

    const tankCertBy = (validationResult.formProgress?.tankCertification?.completedBy || '').trim().toLowerCase();
    const currentInspector = (formData.inspectorName || '').trim().toLowerCase();
    if (tankCertBy && currentInspector && tankCertBy === currentInspector) {
      setSerialModalMessage(
        'Fresh Eyes Required: The person who completed the Tank Certification for this vessel cannot also complete the Final QC Checklist. A different inspector must carry out this check to ensure independent quality verification.'
      );
      setSerialModalOpen(true);
      setIsSubmitting(false);
      return;
    }

    const fp = validationResult.formProgress || {};
    const required = [
      { key: 'preInspection', label: 'Pre Inspection' },
      { key: 'paintQualityInspection', label: 'Paint Quality Inspection' },
      { key: 'tankCertification', label: 'Tank Certification' }
    ];
    const incomplete = required.filter(f => !fp[f.key]?.completed).map(f => f.label);
    if (incomplete.length > 0) {
      setSerialModalMessage(`Cannot submit Final QC Checklist. The following forms must be completed first: ${incomplete.join(', ')}.`);
      setSerialModalOpen(true);
      setIsSubmitting(false);
      return;
    }

    try {
      await sendQCChecklistEmail(formData);
      await markFormCompleted(formData.serialNumber, 'finalQCChecklist', formData.dateCompleted, { completedBy: formData.inspectorName });
      setFormData({
        serialNumber: '',
        customerName: '',
        waterCapacity: '',
        dateCompleted: '',
        qcInspector: '',
        vesselStatus: 'Under Vacuum',
        checklistItems: {
          serialNumberStamped: false,
          dataPlatePresent: false,
          testCertificateAttached: false,
          paintFinishComplete: false,
          ralColourCorrect: false,
          labelsDecalsApplied: false,
          valvesInstalledTorqued: false,
          valvesSealed: false,
          reliefValvePresent: false,
          multivalveOrientation: false,
          protectiveHoodInstalled: false,
          tankSafeTransport: false,
          liftingLugsInspected: false,
          noDamageHandling: false
        },
        inspectorName: '',
        inspectorDate: '',
        inspectorSignature: '',
        secondCheck: '',
        notes: ''
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting QC checklist:', error);
      setSubmitMessage(error.message || 'Error submitting QC checklist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">Final QC Checklist</h1>
                <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-QCC-001 | Version: 1.0</p>
              </div>
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                title="Return to Forms"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">Quality Control Inspection Form</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                    Serial Number *
                  </label>
                  <input
                    type="text"
                    name="serialNumber"
                    value={formData.serialNumber}
                    onChange={handleInputChange}
                    onBlur={handleSerialLookup}
                    className="block w-full pvs-input"
                    required
                  />
                  {serialLookupState.loading && (
                    <p className="mt-1 text-xs text-pvs-text-dim">Looking up vessel…</p>
                  )}
                  {!serialLookupState.loading && serialLookupState.notFound && (
                    <p className="mt-1 text-xs text-red-400">Serial number not found in Vessel Inventory.</p>
                  )}
                  {!serialLookupState.loading && serialLookupState.scrapped && (
                    <p className="mt-1 text-xs text-red-400">{getScrappedFormMessage('finalQCChecklist')}</p>
                  )}
                  {!serialLookupState.loading && freshEyesBlock.blocked && (
                    <div className="mt-2 p-3 rounded-md bg-red-900/30 border border-red-500/50">
                      <p className="text-sm font-semibold text-red-400">
                        Fresh Eyes Required
                      </p>
                      <p className="text-xs text-red-300 mt-1">
                        You ({freshEyesBlock.completedBy}) completed the Tank Certification for this vessel and cannot also complete the Final QC Checklist. A different inspector must carry out this check to ensure independent quality verification.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    placeholder={missingVesselFields.customerName ? MISSING_PLACEHOLDER : ''}
                    className="block w-full pvs-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                    Water Capacity *
                  </label>
                  <input
                    type="number"
                    name="waterCapacity"
                    value={formData.waterCapacity}
                    onChange={handleInputChange}
                    min="0"
                    placeholder={missingVesselFields.waterCapacity ? MISSING_PLACEHOLDER : ''}
                    className="block w-full pvs-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                    Date Completed *
                  </label>
                  <input
                    type="date"
                    name="dateCompleted"
                    value={formData.dateCompleted}
                    onChange={handleInputChange}
                    className="block w-full pvs-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                    QC Inspector *
                  </label>
                  <input
                    type="text"
                    name="qcInspector"
                    value={formData.qcInspector}
                    onChange={handleInputChange}
                    className="block w-full pvs-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                    Vessel Status
                  </label>
                  <select
                    name="vesselStatus"
                    value={formData.vesselStatus}
                    onChange={handleInputChange}
                    className="block w-full pvs-select"
                  >
                    <option value="Under Vacuum">Under Vacuum</option>
                    <option value="Gas Charged">Gas Charged</option>
                  </select>
                </div>
              </div>
            </div>

            {/* LPG Tank Final Quality Control Checklist */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">LPG Tank Final Quality Control Checklist</h2>
              
              {/* 1. Vessel Identity & Paperwork */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-pvs-accent mb-3">1. Vessel Identity & Paperwork</h3>
                <div className="space-y-3 ml-4">
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.serialNumberStamped" checked={formData.checklistItems.serialNumberStamped} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Serial number stamped & matches records</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.dataPlatePresent" checked={formData.checklistItems.dataPlatePresent} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Data plate present and legible</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.testCertificateAttached" checked={formData.checklistItems.testCertificateAttached} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Test certificate attached to vessel file</label>
                  </div>
                </div>
              </div>

              {/* 2. External Condition */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-pvs-accent mb-3">2. External Condition</h3>
                <div className="space-y-3 ml-4">
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.paintFinishComplete" checked={formData.checklistItems.paintFinishComplete} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Paint finish complete (no pinholes, drips, rust bloom, or missed areas)</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.ralColourCorrect" checked={formData.checklistItems.ralColourCorrect} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">RAL colour correct to customer specification</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.labelsDecalsApplied" checked={formData.checklistItems.labelsDecalsApplied} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Labels, decals & safety notices applied</label>
                  </div>
                </div>
              </div>

              {/* 3. Fittings & Accessories */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-pvs-accent mb-3">3. Fittings & Accessories</h3>
                <div className="space-y-3 ml-4">
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.valvesInstalledTorqued" checked={formData.checklistItems.valvesInstalledTorqued} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Valves installed and torqued to specification</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.valvesSealed" checked={formData.checklistItems.valvesSealed} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Valves sealed/bunged to prevent ingress</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.reliefValvePresent" checked={formData.checklistItems.reliefValvePresent} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Relief valve present and test date marked</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.multivalveOrientation" checked={formData.checklistItems.multivalveOrientation} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Multivalve orientation correct</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.protectiveHoodInstalled" checked={formData.checklistItems.protectiveHoodInstalled} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Protective hood/cover securely installed, hinges & catches free-moving</label>
                  </div>
                </div>
              </div>

              {/* 4. Safety & Compliance */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-pvs-accent mb-3">4. Safety & Compliance</h3>
                <div className="space-y-3 ml-4">
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.tankSafeTransport" checked={formData.checklistItems.tankSafeTransport} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Tank safe for transport</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.liftingLugsInspected" checked={formData.checklistItems.liftingLugsInspected} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">Lifting lugs, saddles, and base inspected for defects</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="checklistItems.noDamageHandling" checked={formData.checklistItems.noDamageHandling} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded" />
                    <label className="ml-2 text-sm text-pvs-text-muted">No damage from handling/transport preparation</label>
                  </div>
                </div>
              </div>
            </div>

            {/* Sign-off Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">6. Sign-off</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Inspector Name *</label>
                  <input type="text" name="inspectorName" value={formData.inspectorName} onChange={handleInputChange} className="block w-full pvs-input" placeholder="Inspector Name" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Date *</label>
                  <input type="date" name="inspectorDate" value={formData.inspectorDate} onChange={handleInputChange} className="block w-full pvs-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Signature</label>
                  <input type="text" name="inspectorSignature" value={formData.inspectorSignature} onChange={handleInputChange} className="block w-full pvs-input" placeholder="Digital signature or initials" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Second Check (if applicable)</label>
                  <input type="text" name="secondCheck" value={formData.secondCheck} onChange={handleInputChange} className="block w-full pvs-input" placeholder="Second checker name/signature" />
                </div>
              </div>
            </div>

            {/* Additional Notes Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Additional Notes</h2>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Notes</label>
                <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={4} className="block w-full pvs-input" placeholder="Any additional notes, defects found, or corrective actions taken..." />
              </div>
            </div>

            {submitMessage && (
              <div className="p-4 rounded-md bg-red-900/30 border border-red-500/30 text-red-400">
                {submitMessage}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-6">
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50"
              >
                Back to Forms
              </button>
              
              <button
                type="submit"
                disabled={isSubmitting || freshEyesBlock.blocked}
                title={freshEyesBlock.blocked ? 'Blocked by Fresh Eyes policy — a different inspector must complete this checklist.' : ''}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-semibold text-pvs-bg bg-pvs-accent hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit QC Checklist'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <SerialValidationModal isOpen={serialModalOpen} message={serialModalMessage} onClose={closeSerialModal} />
      <FormSuccessModal isOpen={showSuccessModal} formName="Final QC Checklist" onClose={onBack} />
    </PVSPageLayout>
  );
};

export default FinalQCChecklistForm;
